import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaUsers, 
  FaCalendarAlt, 
  FaSave, 
  FaCalculator,
  FaFileExcel,
  FaPrint,
  FaEdit
} from 'react-icons/fa';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import MaterialButton from '../../components/Buttons/MaterialButton';
import CardComponent from '../../components/cards/CardComponent';
import fetchData from '../../functions/fetchData';

const EmployeePayrollSystem = () => {
  // Core state
  const [departmentList, setDepartmentList] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [activeDepartment, setActiveDepartment] = useState('');
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [activeTheme, setActiveTheme] = useState('ag-theme-quartz');
  
  // Grid reference
  const agGridRef = useRef();

  // Theme options
  const themeOptions = [
    { key: 'ag-theme-quartz', name: 'Quartz (Modern)' },
    { key: 'ag-theme-alpine', name: 'Alpine (Clean)' },
    { key: 'ag-theme-balham', name: 'Balham (Corporate)' },
    { key: 'ag-theme-material', name: 'Material Design' }
  ];

  // Load departments from database
  const loadDepartments = async () => {
    try {
      const departments = await fetchData('employee_departments', null, 'id', {});
      setDepartmentList(departments || []);
    } catch (error) {
      toast.error('Error loading departments');
      console.error('Department fetch error:', error);
    }
  };

  // Cell renderer for currency values
  const CurrencyDisplay = ({ value }) => {
    const amount = parseFloat(value) || 0;
    return (
      <span style={{ 
        color: amount >= 0 ? '#28a745' : '#dc3545', 
        fontWeight: '600',
        fontSize: '11px'
      }}>
        ฿{Math.abs(amount).toLocaleString('th-TH')}
      </span>
    );
  };

  // Cell renderer for status badges
  const StatusBadge = ({ value, type = 'default' }) => {
    const badgeColors = {
      default: { bg: '#6c757d', text: '#fff' },
      success: { bg: '#28a745', text: '#fff' },
      warning: { bg: '#ffc107', text: '#000' },
      danger: { bg: '#dc3545', text: '#fff' },
      info: { bg: '#17a2b8', text: '#fff' }
    };
    
    const colors = badgeColors[type] || badgeColors.default;
    
    return (
      <span style={{
        backgroundColor: colors.bg,
        color: colors.text,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '9px',
        fontWeight: '600'
      }}>
        {value}
      </span>
    );
  };

  // Cell renderer for employee names
  const EmployeeNameDisplay = ({ value }) => (
    <div style={{
      fontWeight: '700',
      color: '#2c3e50',
      fontSize: '11px',
      padding: '4px 0'
    }}>
      {value}
    </div>
  );

  // Grid column configuration
  const gridColumns = [
    // Employee identification section
    {
      headerName: '👤 Employee',
      field: 'fullName',
      width: 200,
      pinned: 'left',
      cellRenderer: EmployeeNameDisplay,
      cellStyle: {
        backgroundColor: '#f8f9fa',
        borderRight: '3px solid #007bff',
        fontWeight: 'bold'
      }
    },
    {
      headerName: 'Code',
      field: 'empCode',
      width: 100,
      pinned: 'left',
      cellStyle: {
        backgroundColor: '#f8f9fa',
        borderRight: '3px solid #007bff',
        textAlign: 'center',
        fontFamily: 'monospace',
        fontSize: '10px'
      }
    },
    {
      headerName: 'Position',
      field: 'jobPosition',
      width: 150,
      pinned: 'left',
      cellStyle: {
        backgroundColor: '#f8f9fa',
        borderRight: '4px solid #007bff',
        fontSize: '10px'
      }
    },
    
    // Salary information section
    {
      headerName: '💰 Payment Type',
      field: 'paymentType',
      width: 120,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Monthly', 'half-monthly']
      },
      cellRenderer: (params) => (
        <StatusBadge 
          value={params.value} 
          type={params.value === 'Monthly' ? 'success' : params.value === 'half-monthly' ? 'info' : 'default'} 
        />
      ),
      cellStyle: { textAlign: 'center', backgroundColor: '#fff9c4' }
    },
    {
      headerName: 'Base Salary',
      field: 'baseSalary',
      width: 130,
      editable: true,
      cellRenderer: (params) => <CurrencyDisplay value={params.value} />,
      cellStyle: { textAlign: 'right', backgroundColor: '#e8f5e8' }
    },
    {
      headerName: 'Daily Rate',
      field: 'dailyRate',
      width: 110,
      cellRenderer: (params) => <CurrencyDisplay value={params.value} />,
      cellStyle: { textAlign: 'right', backgroundColor: '#e1f5fe' }
    },
    
    // Attendance section
    {
      headerName: '📅 Month Days',
      field: 'daysInMonth',
      width: 100,
      cellStyle: {
        textAlign: 'center',
        backgroundColor: '#fff3e0',
        fontWeight: 'bold',
        fontSize: '11px'
      }
    },
    {
      headerName: 'Work Days',
      field: 'workingDays',
      width: 100,
      editable: true,
      cellRenderer: (params) => {
        const value = params.value;
        const paymentType = params.data.paymentType;
        const isHalfMonthly = paymentType === 'half-monthly';
        
        return (
          <span style={{
            fontSize: '11px',
            fontWeight: 'bold',
            color: isHalfMonthly ? '#1976d2' : '#2e7d32',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            {value}
            {isHalfMonthly && (
              <span style={{ 
                marginLeft: '4px', 
                fontSize: '8px', 
                backgroundColor: '#1976d2', 
                color: 'white', 
                padding: '1px 3px', 
                borderRadius: '8px' 
              }}>
                ½M
              </span>
            )}
          </span>
        );
      },
      cellStyle: (params) => {
        const isHalfMonthly = params.data.paymentType === 'half-monthly';
        return {
          textAlign: 'center',
          backgroundColor: isHalfMonthly ? '#e3f2fd' : '#e8f5e8',
          fontWeight: 'bold',
          fontSize: '11px'
        };
      }
    },
    {
      headerName: 'Absent',
      field: 'absentCount',
      width: 80,
      editable: true,
      cellStyle: {
        textAlign: 'center',
        backgroundColor: '#ffebee',
        color: '#c62828',
        fontWeight: 'bold',
        fontSize: '11px'
      }
    },
    {
      headerName: 'Late',
      field: 'lateCount',
      width: 70,
      editable: true,
      cellStyle: {
        textAlign: 'center',
        backgroundColor: '#fff8e1',
        color: '#f57c00',
        fontWeight: 'bold',
        fontSize: '11px'
      }
    },
    {
      headerName: 'Overtime',
      field: 'overtimeHours',
      width: 90,
      editable: true,
      cellStyle: {
        textAlign: 'center',
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        fontWeight: 'bold',
        fontSize: '11px'
      }
    },
    
    // Issues and deductions section
    {
      headerName: '⚠️ SSO',
      field: 'ssoDeduction',
      width: 80,
      editable: true,
      cellRenderer: (params) => {
        const value = params.value || 0;
        return (
          <StatusBadge 
            value={value === 0 ? '✓' : value.toString()} 
            type={value === 0 ? 'success' : 'danger'} 
          />
        );
      },
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: 'No Clock Out',
      field: 'missingClockOut',
      width: 110,
      editable: true,
      cellRenderer: (params) => {
        const value = params.value || 0;
        return (
          <StatusBadge 
            value={value === 0 ? '✓' : value.toString()} 
            type={value === 0 ? 'success' : 'danger'} 
          />
        );
      },
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: 'No Clock In',
      field: 'missingClockIn',
      width: 110,
      editable: true,
      cellRenderer: (params) => {
        const value = params.value || 0;
        return (
          <StatusBadge 
            value={value === 0 ? '✓' : value.toString()} 
            type={value === 0 ? 'success' : 'danger'} 
          />
        );
      },
      cellStyle: { textAlign: 'center' }
    },
    
    // Financial adjustments section
    {
      headerName: '💸 Pending',
      field: 'pendingDeductions',
      width: 110,
      editable: true,
      cellRenderer: (params) => <CurrencyDisplay value={params.value} />,
      cellStyle: { textAlign: 'right', backgroundColor: '#fff3e0' }
    },
    {
      headerName: 'Deductions',
      field: 'salaryDeductions',
      width: 110,
      editable: true,
      cellRenderer: (params) => <CurrencyDisplay value={params.value} />,
      cellStyle: { textAlign: 'right', backgroundColor: '#ffebee' }
    },
    {
      headerName: 'Advances',
      field: 'advancePayments',
      width: 110,
      editable: true,
      cellRenderer: (params) => <CurrencyDisplay value={params.value} />,
      cellStyle: { textAlign: 'right', backgroundColor: '#f3e5f5' }
    },
    {
      headerName: 'Bonus',
      field: 'bonusAmount',
      width: 100,
      editable: true,
      cellRenderer: (params) => <CurrencyDisplay value={params.value} />,
      cellStyle: { textAlign: 'right', backgroundColor: '#e8f5e8' }
    },
    
    // Comments section
    {
      headerName: '📝 Comments',
      field: 'comments',
      width: 160,
      editable: true,
      cellStyle: {
        fontSize: '10px',
        backgroundColor: '#f8f9fa'
      }
    },
    
    // Final calculation
    {
      headerName: '🧮 Final Salary',
      field: 'calculatedSalary',
      width: 150,
      valueGetter: (params) => {
        const base = parseFloat(params.data.baseSalary) || 0;
        const pending = parseFloat(params.data.pendingDeductions) || 0;
        const deductions = parseFloat(params.data.salaryDeductions) || 0;
        const advances = parseFloat(params.data.advancePayments) || 0;
        const bonus = parseFloat(params.data.bonusAmount) || 0;
        const final = base - pending - deductions - advances + bonus;
        return final.toFixed(2);
      },
      cellRenderer: (params) => (
        <span style={{
          fontSize: '12px',
          fontWeight: '800',
          color: '#2e7d32',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          ฿{parseFloat(params.value || 0).toLocaleString('th-TH')}
        </span>
      ),
      cellStyle: {
        textAlign: 'right',
        backgroundColor: '#e8f5e8',
        border: '2px solid #4caf50'
      }
    }
  ];

  // Grid default settings
  const defaultColumnSettings = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      fontSize: '10px',
      padding: '4px 6px'
    }
  };

  // Grid event handlers
  const handleGridReady = (event) => {
    console.log('Payroll grid initialized with records:', employeeRecords.length);
    if (event.api && employeeRecords.length > 0) {
      event.api.sizeColumnsToFit();
    }
  };

  const handleCellEdit = (event) => {
    console.log('Cell edited:', event.colDef.field, '→', event.newValue);
    
    // Special handling for payment type changes
    if (event.colDef.field === 'paymentType') {
      const rowNode = event.node;
      const rowData = rowNode.data;
      
      // Update working days based on payment type
      if (event.newValue === 'half-monthly') {
        rowData.workingDays = 15;
        console.log('Payment type changed to half-monthly, setting working days to 15');
      } else if (event.newValue === 'Monthly') {
        rowData.workingDays = rowData.daysInMonth;
        console.log('Payment type changed to Monthly, setting working days to', rowData.daysInMonth);
      }
      
      // Update the row data
      rowNode.setData(rowData);
    }
    
    // Trigger recalculation of dependent fields
    event.api.refreshCells({ force: true });
  };

  // Export functions
  const exportPayrollData = () => {
    if (agGridRef.current && agGridRef.current.api) {
      agGridRef.current.api.exportDataAsExcel({
        fileName: `Payroll_${activeDepartment}_${currentMonth}_${new Date().getTime()}.xlsx`,
        sheetName: 'Payroll Data'
      });
      toast.success('Payroll data exported successfully!');
    }
  };

  const printPayrollReport = () => {
    if (agGridRef.current && agGridRef.current.api) {
      // Open print dialog with current grid view
      window.print();
      toast.info('Print dialog opened');
    }
  };

  const savePayrollChanges = async () => {
    try {
      // Here you would implement the save logic
      console.log('Saving payroll changes:', employeeRecords);
      toast.success('Payroll changes saved successfully!');
    } catch (error) {
      toast.error('Failed to save payroll changes');
      console.error('Save error:', error);
    }
  };

  // Calculate totals for statistics
  const calculateStatistics = () => {
    if (employeeRecords.length === 0) return { totalEmployees: 0, totalSalary: 0, totalDeductions: 0 };
    
    const totalSalary = employeeRecords.reduce((sum, record) => 
      sum + (parseFloat(record.baseSalary) || 0), 0);
    
    const totalDeductions = employeeRecords.reduce((sum, record) => 
      sum + (parseFloat(record.pendingDeductions) || 0) + 
            (parseFloat(record.salaryDeductions) || 0) + 
            (parseFloat(record.advancePayments) || 0), 0);
    
    return {
      totalEmployees: employeeRecords.length,
      totalSalary,
      totalDeductions
    };
  };

  const stats = calculateStatistics();

  // Effects
  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    if (activeDepartment) {
      const loadEmployees = async () => {
        try {
          setIsLoading(true);
          setEmployeeRecords([]);
          
          const employeeQuery = `
            SELECT 
              e.*,
              ep.position_name,
              ep.base_salary as position_salary
            FROM employees e 
            LEFT JOIN employee_positions ep ON e.position_id = ep.id 
            WHERE e.department_id = ?
            ORDER BY e.english_first_name, e.thai_first_name
          `;
          
          const employees = await fetchData('employees', employeeQuery, 'id', {
            department_id: activeDepartment
          });
          
          const employeeList = employees || [];
          console.log('Loaded employees for department:', employeeList);
          setEmployeeData(employeeList);
          
          if (employeeList.length === 0) {
            toast.info('No employees found in selected department');
          } else {
            toast.success(`Successfully loaded ${employeeList.length} employees`);
          }
        } catch (error) {
          toast.error('Error loading employees');
          console.error('Employee fetch error:', error);
          setEmployeeData([]);
          setEmployeeRecords([]);
        } finally {
          setIsLoading(false);
        }
      };
      
      loadEmployees();
    }
  }, [activeDepartment]);

  useEffect(() => {
    if (employeeData.length > 0) {
      // Calculate days in month
      const getMonthDays = () => {
        if (!currentMonth) return 30;
        const [year, month] = currentMonth.split('-').map(Number);
        return new Date(year, month, 0).getDate();
      };
      
      const monthDays = getMonthDays();
      
      const records = employeeData.map((emp, index) => {
        // Employee name handling
        const thaiFullName = `${emp.thai_first_name || ''} ${emp.thai_last_name || ''}`.trim();
        const englishFullName = `${emp.english_first_name || ''} ${emp.english_last_name || ''}`.trim();
        const displayName = englishFullName || thaiFullName || `Employee ${emp.id}`;
        
        // Salary calculations
        const monthlySalary = parseFloat(emp.salary_amount || emp.position_salary || 0);
        const salaryType = emp.salary_type || 'Monthly';
        const dailySalary = monthlySalary > 0 ? (monthlySalary / monthDays) : 0;
        
        // Determine working days based on salary type
        const workingDays = salaryType.toLowerCase() === 'half-monthly' ? 15 : monthDays;
        
        return {
          recordId: `emp_${emp.id}_${index}`,
          employeeDbId: emp.id,
          fullName: displayName,
          empCode: emp.employee_id || `EMP${String(emp.id).padStart(4, '0')}`,
          jobPosition: emp.position_name || 'Not Assigned',
          paymentType: salaryType,
          baseSalary: monthlySalary,
          dailyRate: dailySalary,
          daysInMonth: monthDays,
          workingDays: workingDays,
          absentCount: 0,
          lateCount: 0,
          overtimeHours: 0,
          ssoDeduction: 0,
          missingClockOut: 0,
          missingClockIn: 0,
          pendingDeductions: 0,
          salaryDeductions: 0,
          advancePayments: 0,
          bonusAmount: 0,
          comments: '',
          calculatedSalary: monthlySalary
        };
      });
      
      console.log('Built payroll records:', records);
      setEmployeeRecords(records);
    }
  }, [currentMonth, employeeData]);

  return (
    <Layout>
      <Header title="Employee Payroll System" />
      
      <div className="container-fluid px-4">
        {/* Control Panel */}
        <div className="row mb-4">
          <div className="col-12">
            <CardComponent>
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <FaCalculator className="me-2" />
                  Payroll Configuration
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-bold">
                      <FaUsers className="me-2 text-primary" />
                      Department Selection
                    </label>
                    <select
                      className="form-select"
                      value={activeDepartment}
                      onChange={(e) => setActiveDepartment(e.target.value)}
                      disabled={isLoading}
                    >
                      <option value="">Select Department</option>
                      {departmentList.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.department_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-md-2">
                    <label className="form-label fw-bold">
                      <FaCalendarAlt className="me-2 text-success" />
                      Payroll Period
                    </label>
                    <input
                      type="month"
                      className="form-control"
                      value={currentMonth}
                      onChange={(e) => setCurrentMonth(e.target.value)}
                    />
                  </div>
                  
                  <div className="col-md-2">
                    <label className="form-label fw-bold">
                      Display Theme
                    </label>
                    <select
                      className="form-select"
                      value={activeTheme}
                      onChange={(e) => setActiveTheme(e.target.value)}
                    >
                      {themeOptions.map(theme => (
                        <option key={theme.key} value={theme.key}>
                          {theme.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-md-5">
                    <div className="d-flex gap-2 justify-content-end">
                      <MaterialButton
                        variant="success"
                        onClick={exportPayrollData}
                        disabled={!activeDepartment || employeeRecords.length === 0}
                      >
                        <FaFileExcel className="me-1" />
                        Export
                      </MaterialButton>
                      
                      <MaterialButton
                        variant="info"
                        onClick={printPayrollReport}
                        disabled={!activeDepartment || employeeRecords.length === 0}
                      >
                        <FaPrint className="me-1" />
                        Print
                      </MaterialButton>
                      
                      <MaterialButton
                        variant="primary"
                        onClick={savePayrollChanges}
                        disabled={!activeDepartment || employeeRecords.length === 0}
                      >
                        <FaSave className="me-1" />
                        Save
                      </MaterialButton>
                    </div>
                  </div>
                </div>
              </div>
            </CardComponent>
          </div>
        </div>

        {/* Statistics Dashboard */}
        {activeDepartment && employeeRecords.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-3">
              <CardComponent>
                <div className="card-body text-center bg-light">
                  <h3 className="text-primary fw-bold">{stats.totalEmployees}</h3>
                  <p className="mb-0 text-muted">Total Employees</p>
                </div>
              </CardComponent>
            </div>
            <div className="col-md-3">
              <CardComponent>
                <div className="card-body text-center bg-light">
                  <h3 className="text-success fw-bold">
                    ฿{stats.totalSalary.toLocaleString('th-TH')}
                  </h3>
                  <p className="mb-0 text-muted">Total Base Salary</p>
                </div>
              </CardComponent>
            </div>
            <div className="col-md-3">
              <CardComponent>
                <div className="card-body text-center bg-light">
                  <h3 className="text-danger fw-bold">
                    ฿{stats.totalDeductions.toLocaleString('th-TH')}
                  </h3>
                  <p className="mb-0 text-muted">Total Deductions</p>
                </div>
              </CardComponent>
            </div>
            <div className="col-md-3">
              <CardComponent>
                <div className="card-body text-center bg-light">
                  <h3 className="text-info fw-bold">
                    {currentMonth ? new Date(currentMonth.split('-')[0], currentMonth.split('-')[1], 0).getDate() : 30}
                  </h3>
                  <p className="mb-0 text-muted">Days in {currentMonth}</p>
                </div>
              </CardComponent>
            </div>
          </div>
        )}

        {/* Main Payroll Grid */}
        {activeDepartment && employeeRecords.length > 0 ? (
          <div className="row">
            <div className="col-12">
              <CardComponent>
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">
                    <FaEdit className="me-2" />
                    Payroll Data Grid - {departmentList.find(d => d.id === activeDepartment)?.department_name}
                    <span className="badge bg-light text-dark ms-2">
                      {employeeRecords.length} employees
                    </span>
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className={`${activeTheme}`} style={{ height: '650px', width: '100%' }}>
                    <AgGridReact
                      ref={agGridRef}
                      rowData={employeeRecords}
                      columnDefs={gridColumns}
                      defaultColDef={defaultColumnSettings}
                      onGridReady={handleGridReady}
                      onCellValueChanged={handleCellEdit}
                      getRowId={(params) => params.data.recordId}
                      rowHeight={36}
                      headerHeight={42}
                      pagination={true}
                      paginationPageSize={25}
                      loading={isLoading}
                      animateRows={true}
                      enableCellTextSelection={true}
                      suppressLoadingOverlay={false}
                      suppressNoRowsOverlay={false}
                      rowSelection="multiple"
                    />
                  </div>
                </div>
              </CardComponent>
            </div>
          </div>
        ) : activeDepartment ? (
          <div className="row">
            <div className="col-12">
              <CardComponent>
                <div className="card-body text-center py-5">
                  <FaUsers size={64} className="text-muted mb-3" />
                  <h3 className="text-muted">No Employees Found</h3>
                  <p className="text-muted">
                    The selected department doesn't have any employees registered.
                  </p>
                </div>
              </CardComponent>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              <CardComponent>
                <div className="card-body text-center py-5">
                  <FaCalculator size={64} className="text-primary mb-3" />
                  <h3 className="text-primary">Welcome to Employee Payroll System</h3>
                  <p className="text-muted lead">
                    Select a department from the dropdown above to begin managing payroll data.
                  </p>
                  <div className="row mt-4">
                    <div className="col-md-4">
                      <div className="text-center">
                        <FaEdit size={32} className="text-info mb-2" />
                        <h6>Edit Payroll Data</h6>
                        <small className="text-muted">Modify employee salaries, deductions, and bonuses</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center">
                        <FaFileExcel size={32} className="text-success mb-2" />
                        <h6>Export Reports</h6>
                        <small className="text-muted">Generate Excel reports for accounting</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center">
                        <FaCalculator size={32} className="text-warning mb-2" />
                        <h6>Auto Calculations</h6>
                        <small className="text-muted">Automatic salary calculations with deductions</small>
                      </div>
                    </div>
                  </div>
                </div>
              </CardComponent>
            </div>
          </div>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Layout>
  );
};

export default EmployeePayrollSystem;
